// import {zodResolver} from '@hookform/resolvers/zod';
import {Grid, makeStyles, Tab, Tabs, Typography} from '@material-ui/core';
import Card from '@material-ui/core/Card';
import {observer} from 'mobx-react-lite';
import {useHistory, useLocation} from 'react-router-dom';
import {ChangeEvent, FC, useEffect, useMemo, useState} from 'react';
// import {useForm} from 'react-hook-form';
import AgeniumProvider from '@/Core/agenium/ageniumContext';
import {Parameter, ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
import {Quote} from '../../Core/mobx/ParameterStore/Quote';
import RootStore from '../../Core/mobx/RootStore';
import {Spacing} from '../../Core/UI';
import CustomerPortalStore from '../Customer/CustomerPortalStore/CustomerPortalStore';
import {fieldOptions} from '../../Core/mobx/ParameterStore/PrequalXref';
import Basic from '../Questions/Basic';
import GuaranteedTab from './ProductTabs/GuaranteedTab';
import GULTab from './ProductTabs/GULTab';
import IULTab from './ProductTabs/IULTab';
// import {createProsperSchema, ProsperFormValues} from './ProductTabs/lib/validation';
import LSWIULTab from './ProductTabs/LSWIULTab';
import ProsperTab from './ProductTabs/ProsperTab';
import TermTab from './ProductTabs/TermTab';
import VULTab from './ProductTabs/VULTab';
// import {defaultValues} from './ProductTabs/lib/defaultValues';
import {QuoteAPI} from './QuotesAPI';
import WHLTab from './ProductTabs/WHLTab';
import {SelectInputElement} from '../PreQualification/Components/SelectInputElement';
import InstantAppModal, {InstantAppModalPage} from './InstantAppModal';
import {instantAppModalPageRanking} from './utils/instantAppModalPageRanking';
import {useApplicationData} from '../../Core/hooks/useApplicationData';
import ADBTab from './ProductTabs/ADBTab';
import {getCarrierProductFromCode} from '../../Core/utils/productCodeMapping';
import AnnuityTab from './ProductTabs/AnnuityTab';
import {IS_PRODUCTION} from '@/constants';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background.paper,
        '& .MuiTabs-flexContainer': {
            justifyContent: 'space-between',
        },
        '& .MuiTab-root': {
            '@media (min-width: 600px)': {
                minWidth: '20px',
            },
        },
        '& .MuiTab-root:first-child': {
            marginLeft: '20px',
        },
        '& .MuiTab-root:last-child': {
            marginRight: '20px',
        },
    },
    sectionTitleWrapper: {
        display: 'flex',
        alignItems: 'center',
        textTransform: 'capitalize',
    },
}));

interface QuoteQuestionsProps {
    parameterStore: ParameterStore;
    formType: 'single-quote' | 'combination-quote';
    quoteParams: Quote;
    customerPortalStore: CustomerPortalStore;
    nestOnTop?: React.ReactNode;
    nestOnBottom?: React.ReactNode;
}

export const QuoteQuestions: FC<QuoteQuestionsProps> = observer(
    ({parameterStore, formType, quoteParams, customerPortalStore, nestOnTop, nestOnBottom}: QuoteQuestionsProps) => {
        const {prequal, onValueChangedQuote, onValueChangedPreQual, onValueChanged, quoteCombination} =
            parameterStore || {};
        const tabs = ['Term', 'IUL', 'LSWIUL', 'Guaranteed', 'Whole Life', 'Prosper', 'ADB', 'Annuity'];
        const classes = useStyles();
        const shouldShowProsperQuote = process.env.NEXT_PUBLIC_SHOW_PROSPER_QUOTE !== 'false';
        const latestQuote =
            parameterStore.quoteHistory[parameterStore.quoteHistory.length - 1]?.singleQuote?.productType;
        const warningOffset =
            (!IS_PRODUCTION ? 76 : 0) +
            (parameterStore != null && !RootStore.currentAgentStates.includes(parameterStore.approval.state.value)
                ? 80
                : 0);
        const [value, setValue] = useState(() =>
            (latestQuote || shouldShowProsperQuote) && !quoteCombination.isCombination
                ? tabs.findIndex((o) => o === (latestQuote || 'Prosper'))
                : (tabs.findIndex((o) => o === (quoteParams?.productType?.value || 'IUL')) ?? 0),
        );
        const [isAgentAssistRightExpanded, setIsAgentAssistRightExpanded] = useState(true);
        const history = useHistory();
        const location = useLocation();
        const {applicationData, updateApplicationData} = useApplicationData();
        const searchParams = new URLSearchParams(location.search);
        const modalPage = searchParams.get('modalPage')?.toUpperCase() as InstantAppModalPage | null;
        const isModalVisible = !!modalPage;
        const quoteId = Number(searchParams.get('quoteId'));
        const quote = parameterStore.quoteHistory.find((q) => q.id === quoteId);
        const quoteData = formType === 'single-quote' ? quote?.singleQuote?.out : quote?.combinedQuote?.out;

        // Get productType from URL parameter
        const urlProductType = searchParams.get('productType');

        // Set the tab based on URL parameter
        useEffect(() => {
            if (urlProductType) {
                const tabIndex = tabs.findIndex((tab) => tab === urlProductType);
                if (tabIndex !== -1 && tabIndex !== value) {
                    console.log('Setting tab from URL parameter:', urlProductType, 'index:', tabIndex);
                    setValue(tabIndex);
                    // Also update the productType in the store
                    if (quoteParams?.productType) {
                        onValueChanged(null, quoteParams.productType, urlProductType);
                    }
                }

                // Try to set the carrier product based on the selected product code from prequal
                const approval = parameterStore?.approval;
                const selectedProductCode =
                    approval?.selectedProductCode?.value || approval?.firstCarrierQuotedCode?.value;

                if (selectedProductCode) {
                    const carrierProductMapping = getCarrierProductFromCode(selectedProductCode);

                    if (carrierProductMapping) {
                        // Set the appropriate carrier product field based on product type
                        if (urlProductType === 'IUL' && quoteParams?.productIUL) {
                            console.log('Setting IUL carrier product:', carrierProductMapping.carrierProduct);
                            onValueChanged(null, quoteParams.productIUL, carrierProductMapping.carrierProduct);
                        } else if (urlProductType === 'Term' && quoteParams?.productTerm) {
                            console.log('Setting Term carrier product:', carrierProductMapping.carrierProduct);
                            onValueChanged(null, quoteParams.productTerm, carrierProductMapping.carrierProduct);
                        } else if (urlProductType === 'VUL' && quoteParams?.productVUL) {
                            console.log('Setting VUL carrier product:', carrierProductMapping.carrierProduct);
                            onValueChanged(null, quoteParams.productVUL, carrierProductMapping.carrierProduct);
                        } else if (urlProductType === 'Guaranteed' && quoteParams?.productGuaranteed) {
                            console.log('Setting Guaranteed carrier product:', carrierProductMapping.carrierProduct);
                            onValueChanged(null, quoteParams.productGuaranteed, carrierProductMapping.carrierProduct);
                        } else if (urlProductType === 'Whole Life' && quoteParams?.productWHL) {
                            console.log('Setting Whole Life carrier product:', carrierProductMapping.carrierProduct);
                            onValueChanged(null, quoteParams.productWHL, carrierProductMapping.carrierProduct);
                        } else if (urlProductType === 'ADB' && quoteParams?.productADB) {
                            console.log('Setting ADB carrier product:', carrierProductMapping.carrierProduct);
                            onValueChanged(null, quoteParams.productADB, carrierProductMapping.carrierProduct);
                        } else if (urlProductType === 'Annuity' && quoteParams?.productAnnuity) {
                            console.log('Setting Annuity carrier product:', carrierProductMapping.carrierProduct);
                            onValueChanged(null, quoteParams.productAnnuity, carrierProductMapping.carrierProduct);
                        }
                        // Note: Prosper and LSWIUL tabs might have different field names
                    }
                }

                // Remove the productType parameter from URL after using it
                const newSearchParams = new URLSearchParams(location.search);
                newSearchParams.delete('productType');
                history.replace({
                    pathname: location.pathname,
                    search: newSearchParams.toString(),
                    hash: location.hash,
                });
            }
        }, [urlProductType]);

        useEffect(() => {
            const newRanking = instantAppModalPageRanking.get(modalPage);
            const oldRanking = instantAppModalPageRanking.get(applicationData?.prosperActiveModalPage);
            if (modalPage && newRanking && (!oldRanking || newRanking >= oldRanking)) {
                updateApplicationData({prosperActiveModalPage: modalPage}, undefined, {mergeWithCurrent: false});
            }
        }, [modalPage]);

        const closeModal = () => {
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete('modalPage');
            searchParams.delete('quoteId');
            history.push({
                pathname: location.pathname,
                search: searchParams.toString(),
                hash: location.hash,
            });
        };

        useEffect(() => {
            const selectedTab = tabs[value];

            if (quoteParams?.productType?.value !== selectedTab) {
                onValueChanged(null, quoteParams?.productType, selectedTab);
            }
        }, [quoteParams?.productType?.value, value]);

        const handleChange = (_event: ChangeEvent<Record<string, unknown>>, newValue: number) => {
            setValue(newValue);
            onValueChanged(null, quoteParams?.productType, tabs[newValue]);
        };

        const changeConditionalValues = (param: any, dependentParam: any) => {
            let log =
                param.name +
                ' is changed to ' +
                param.value +
                ', hence changing ' +
                dependentParam.name +
                ' to ' +
                dependentParam.value;
            RootStore.uiStore.showSnackbar(log, 'info');
            onValueChanged(
                null,
                quoteParams?.[dependentParam.name as keyof typeof quoteParams] as Parameter,
                dependentParam.value,
            );
        };

        // @ToDo Check with Jackson to see if this is needed anymore. Does not look like it is used
        // const prosperForm = useForm<ProsperFormValues>({
        //     resolver: zodResolver(
        //         createProsperSchema(parameterStore.prequal.age.value, parameterStore.approval.gender.value),
        //     ),
        //     mode: 'onChange',
        //     defaultValues: defaultValues(parameterStore),
        // });

        const required = (
            <span>
                <span style={{color: 'red'}}>*</span>
            </span>
        );

        const gridContainerStyle = useMemo(
            () => ({height: `calc(100vh - ${124 + warningOffset}px)`, overflowY: 'hidden' as const}),
            [warningOffset],
        );

        return (
            <Grid container spacing={2} style={quoteCombination.isCombination ? {} : gridContainerStyle}>
                <Grid
                    item
                    xs={quoteCombination.isCombination ? 12 : isAgentAssistRightExpanded ? 8 : 11}
                    style={quoteCombination.isCombination ? {} : {height: '100%', overflowY: 'auto', paddingTop: 20}}
                >
                    {nestOnTop && nestOnTop}
                    <Grid item xs={12} style={{textAlign: 'center'}}>
                        <small>quotes are not available in NY except for LSW term</small>
                    </Grid>
                    <Grid item xs={12}>
                        <div className={classes.root}>
                            <Tabs variant="scrollable" value={value} onChange={handleChange} centered={true}>
                                {tabs
                                    .filter((tab) => shouldShowProsperQuote || tab !== 'Prosper')
                                    .map((tab) => {
                                        let ariaLabel = `quote-${tab}-panel`;
                                        if (formType == 'combination-quote') {
                                            ariaLabel += '-comb';
                                        }
                                        return (
                                            <Tab
                                                key={tab}
                                                label={tab}
                                                aria-label={ariaLabel}
                                                disabled={quoteCombination.isCombination && tab === 'Prosper'}
                                            />
                                        );
                                    })}
                            </Tabs>
                        </div>
                    </Grid>
                    <Grid container item xs={12} spacing={2}>
                        <Grid item xs={quoteCombination.isCombination ? 8 : 12} style={{marginTop: 20}}>
                            <h3>Questions</h3>
                            <Spacing height={20} />
                            {quoteParams?.productType.value !== 'Prosper' && (
                                <Card
                                    style={{
                                        padding: 20,
                                        marginBottom: 20,
                                    }}
                                >
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <div className={classes.sectionTitleWrapper}>
                                                <Typography style={{fontWeight: 'bold', fontSize: 24}}>
                                                    Basic Information
                                                </Typography>
                                            </div>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={3}>
                                        <Basic valueChanged={onValueChangedPreQual} quotes parameters={prequal} />
                                        <Grid item xs={4}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'State',
                                                    options: fieldOptions.state.options,
                                                    ...prequal.state,
                                                    name: 'state',
                                                }}
                                                required={required}
                                                valueChanged={onValueChangedPreQual}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            )}
                            {quoteParams?.productType.value === 'Term' && (
                                <TermTab
                                    parameters={quoteParams}
                                    valueChanged={onValueChangedQuote}
                                    formType={formType}
                                />
                            )}
                            {quoteParams?.productType.value === 'IUL' && (
                                <IULTab
                                    parameters={quoteParams}
                                    refParameters={prequal}
                                    valueChanged={onValueChangedQuote}
                                    formType={formType}
                                    changeConditionalValues={changeConditionalValues}
                                    parameterStore={parameterStore}
                                />
                            )}
                            {quoteParams?.productType.value === 'LSWIUL' && (
                                <LSWIULTab
                                    parameters={quoteParams}
                                    refParameters={prequal}
                                    valueChanged={onValueChangedQuote}
                                    formType={formType}
                                />
                            )}
                            {quoteParams?.productType.value === 'Guaranteed' && (
                                <GuaranteedTab
                                    parameters={quoteParams}
                                    valueChanged={onValueChangedQuote}
                                    formType={formType}
                                />
                            )}
                            {quoteParams?.productType.value === 'VUL' && (
                                <VULTab
                                    parameters={quoteParams}
                                    refParameters={prequal}
                                    valueChanged={onValueChangedQuote}
                                    formType={formType}
                                />
                            )}
                            {quoteParams?.productType.value === 'GUL' && (
                                <GULTab
                                    parameters={quoteParams}
                                    refParameters={prequal}
                                    valueChanged={onValueChangedQuote}
                                    formType={formType}
                                />
                            )}
                            {quoteParams?.productType.value === 'Whole Life' && (
                                <WHLTab
                                    parameters={quoteParams}
                                    refParameters={prequal}
                                    valueChanged={onValueChangedQuote}
                                    formType={formType}
                                    parameterStore={parameterStore}
                                />
                            )}
                            {quoteParams?.productType.value === 'Prosper' && (
                                <ProsperTab
                                    parameters={quoteParams}
                                    valueChanged={onValueChangedQuote}
                                    formType={formType}
                                    parameterStore={parameterStore}
                                />
                            )}
                            {quoteParams?.productType.value === 'ADB' && (
                                <ADBTab
                                    parameterStore={parameterStore}
                                    valueChanged={onValueChangedQuote}
                                    formType={formType}
                                />
                            )}
                            {quoteParams?.productType.value === 'Annuity' && (
                                <AnnuityTab
                                    parameterStore={parameterStore}
                                    valueChanged={onValueChangedQuote}
                                    formType={formType}
                                />
                            )}
                        </Grid>
                        {quoteCombination.isCombination && (
                            <Grid item xs={4} style={{marginTop: 20}}>
                                <QuoteAPI
                                    parameterStore={parameterStore}
                                    formType={formType}
                                    customerPortalStore={customerPortalStore}
                                    hideFoldAgentAssistButton
                                />
                            </Grid>
                        )}
                        {nestOnBottom && nestOnBottom}
                    </Grid>
                </Grid>
                {!quoteCombination.isCombination && (
                    <Grid
                        item
                        xs={isAgentAssistRightExpanded ? 4 : 1}
                        style={{
                            height: '100%',
                            overflowY: 'auto',
                            paddingTop: 20,
                            ...(!isAgentAssistRightExpanded && {
                                display: 'flex',
                                justifyContent: 'center',
                                paddingTop: 0,
                            }),
                        }}
                    >
                        <QuoteAPI
                            parameterStore={parameterStore}
                            formType={formType}
                            customerPortalStore={customerPortalStore}
                            doOnAgentAssistExpand={() => setIsAgentAssistRightExpanded(true)}
                            doOnAgentAssistCollapse={() => setIsAgentAssistRightExpanded(false)}
                        />
                    </Grid>
                )}
                {isModalVisible && quoteData && (
                    <AgeniumProvider quoteResponse={quoteData}>
                        <InstantAppModal
                            onClose={closeModal}
                            parameterStore={parameterStore}
                            quoteResponse={quoteData}
                            formType={formType}
                        />
                    </AgeniumProvider>
                )}
            </Grid>
        );
    },
);
